<template>
    <b-modal
        :id="modalId"
        :title="$t('Shared.Edit')"
        :ok-title="$t('Shared.Save')"
        :cancel-title="$t('Shared.BtnCancel')"
        footer-class="p-2"
        title-class="modal-title"
        @show="initFormValues"
        @ok="handleOk"
    >
        <b-form>
            <b-form-group
                v-for="col in editableColumns"
                :key="col.key"
                :label="col.label"
                label-cols-sm="4"
                label-class="font-weight-bold pt-1"
            >
                <!-- Text -->
                <template v-if="!col.dataType || col.dataType === 'text'">
                    <b-form-input
                        v-model="formValues[col.key]"
                        :placeholder="isMixed[col.key] ? $t('Shared.MultipleValues') : ''"
                        :state="fieldErrors[col.key] ? false : null"
                        trim
                        @input="clearError(col.key)"
                    />
                    <b-form-invalid-feedback v-if="fieldErrors[col.key]" force-show>
                        {{ fieldErrors[col.key] }}
                    </b-form-invalid-feedback>
                </template>

                <!-- Numeric -->
                <template v-else-if="col.dataType === 'numeric'">
                    <b-form-input
                        type="number"
                        v-model="formValues[col.key]"
                        :step="numericProp(col, 'step', 1)"
                        :min="numericProp(col, 'min', null)"
                        :max="numericProp(col, 'max', null)"
                        :placeholder="isMixed[col.key] ? $t('Shared.MultipleValues') : ''"
                        :state="fieldErrors[col.key] ? false : null"
                        @input="clearError(col.key)"
                    />
                    <b-form-invalid-feedback v-if="fieldErrors[col.key]" force-show>
                        {{ fieldErrors[col.key] }}
                    </b-form-invalid-feedback>
                </template>

                <!-- Dropdown -->
                <template v-else-if="col.dataType === 'dropdown'">
                    <b-form-select
                        v-model="formValues[col.key]"
                        :options="dropdownOptions(col)"
                        :state="fieldErrors[col.key] ? false : null"
                        @change="clearError(col.key)"
                    />
                    <b-form-invalid-feedback v-if="fieldErrors[col.key]" force-show>
                        {{ fieldErrors[col.key] }}
                    </b-form-invalid-feedback>
                </template>

                <!-- Lookup -->
                <template v-else-if="col.dataType === 'lookup'">
                    <MdLookup
                        :Label="null"
                        :classValueStyle="''"
                        :Api="col.props && col.props.api"
                        :FieldCode="(col.props && col.props.fieldCode) || 'code'"
                        :FieldDescription="(col.props && col.props.fieldDescription) || 'description'"
                        :SearchFieldCode="col.props && col.props.searchFieldCode"
                        :SearchTitle="col.props && col.props.searchTitle"
                        :Fields="col.props && col.props.fields"
                        :AuxFields="col.props && col.props.auxFields"
                        :RefCode="lookupRefCode(col.key)"
                        :ModelCode="formValues[col.key]"
                        :ModelDescription="formDescriptions[col.key]"
                        :GetValues="result => onLookupChange(col, result)"
                    />
                    <small v-if="fieldErrors[col.key]" class="text-danger d-block mt-1">
                        {{ fieldErrors[col.key] }}
                    </small>
                </template>
            </b-form-group>
        </b-form>
    </b-modal>
</template>

<script>
import MdLookup from '../lookup/MdLookup.vue';

export default {
    name: 'EditableBulkEditModal',
    components: { MdLookup },
    props: {
        modalId: { type: String, required: true },
        columns: { type: Array, required: true },
        selectedRows: { type: Array, default: () => [] }
    },
    data() {
        return {
            formValues: {},
            formDescriptions: {},
            isMixed: {},
            fieldErrors: {}
        };
    },
    computed: {
        editableColumns() {
            return this.columns.filter(col => col.editable !== false);
        }
    },
    methods: {
        initFormValues() {
            const values = {};
            const descriptions = {};
            const mixed = {};

            this.editableColumns.forEach(col => {
                const colValues = this.selectedRows.map(row => row[col.key]);
                const allSame =
                    colValues.length > 0 && colValues.every(v => v === colValues[0]);

                values[col.key] = allSame ? colValues[0] : null;
                mixed[col.key] = !allSame;

                if (col.dataType === 'lookup' && col.props && col.props.valueDescription) {
                    const colDescs = this.selectedRows.map(
                        row => row[col.props.valueDescription]
                    );
                    const allDescSame =
                        colDescs.length > 0 && colDescs.every(d => d === colDescs[0]);
                    descriptions[col.key] = allDescSame ? colDescs[0] : null;
                }
            });

            this.formValues = values;
            this.formDescriptions = descriptions;
            this.isMixed = mixed;
            this.fieldErrors = {};
        },

        clearError(colKey) {
            if (this.fieldErrors[colKey]) {
                this.$delete(this.fieldErrors, colKey);
            }
        },

        dropdownOptions(col) {
            const props = col.props || {};
            const { options = [], optionKey = 'key', optionValue = 'value' } = props;
            const placeholder = this.isMixed[col.key]
                ? `(${this.$t('Shared.MultipleValues')})`
                : `(${this.$t('MdSelect.sOption')})`;
            return [
                { value: null, text: placeholder },
                ...options.map(opt => ({ value: opt[optionKey], text: opt[optionValue] }))
            ];
        },

        numericProp(col, propName, fallback) {
            return col.props && col.props[propName] !== undefined
                ? col.props[propName]
                : fallback;
        },

        lookupRefCode(colKey) {
            return this.modalId.replace(/-/g, '') + '_' + colKey;
        },

        onLookupChange(col, result) {
            this.$set(this.formValues, col.key, result.code || null);
            this.clearError(col.key);
            if (col.props && col.props.valueDescription) {
                this.$set(this.formDescriptions, col.key, result.description || null);
            }
        },

        handleOk(bvModalEvent) {
            bvModalEvent.preventDefault();

            const editedValues = {};
            const errors = {};

            this.editableColumns.forEach(col => {
                const val = this.formValues[col.key];
                const hasValue = val !== null && val !== undefined && val !== '';

                if (hasValue && col.validator && col.validator.length > 0) {
                    for (const rule of col.validator) {
                        if (!rule.validate(String(val))) {
                            errors[col.key] = rule.message;
                            break;
                        }
                    }
                }

                if (hasValue && !errors[col.key]) {
                    editedValues[col.key] =
                        col.dataType === 'numeric' ? Number(val) : val;

                    if (col.dataType === 'lookup' && col.props && col.props.valueDescription) {
                        editedValues[col.props.valueDescription] =
                            this.formDescriptions[col.key] ?? null;
                    }
                }
            });

            this.fieldErrors = errors;

            if (Object.keys(errors).length > 0) {
                return;
            }

            this.$emit('apply', editedValues);
            this.$nextTick(() => this.$bvModal.hide(this.modalId));
        }
    }
};
</script>
